import { useEffect, useState, useCallback } from "react";
import { FileText, Plus } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import { Loader, EmptyState, StatusBadge } from "@/components/common";
import { QUOTE_STATUS } from "@/lib/labels";
import QuoteRequestModal from "@/components/QuoteRequestModal";
import { toast } from "sonner";

export default function QuotesPage() {
  const [quotes, setQuotes] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => { api.get("/quotes").then((r) => setQuotes(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const respond = async (qid, action) => {
    try { await api.post(`/quotes/${qid}/respond`, null, { params: { action } }); toast.success("Réponse enregistrée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!quotes) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-3xl font-extrabold text-[#0F172A]">Mes devis</h1>
        <button onClick={() => setShowNew(true)} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-1" data-testid="new-quote-btn"><Plus className="w-4 h-4" /> Demander</button>
      </div>

      {quotes.length === 0 ? (
        <EmptyState icon={FileText} title="Aucune demande de devis" subtitle="Demandez un devis pour vos installations complexes."
          action={<button onClick={() => setShowNew(true)} className="bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg">Demander un devis</button>} />
      ) : (
        <div className="space-y-3">
          {quotes.map((q) => (
            <div key={q.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`quote-row-${q.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-num font-bold text-slate-800">{q.quote_number}</span>
                <StatusBadge map={QUOTE_STATUS} value={q.status} />
              </div>
              <p className="text-sm font-semibold text-slate-700 mt-1">{q.product_name} — {q.quantity} unité(s)</p>
              <p className="text-xs text-slate-500 mt-0.5">{q.description}</p>
              <p className="text-xs text-slate-400 mt-1">Demandé le {formatDate(q.created_at)} · Zone {q.zone}</p>
              {q.status === "envoye" && (
                <div className="mt-3 bg-[#FDF3EE] rounded-lg p-3">
                  <p className="text-sm">Montant proposé : <strong className="font-num text-[#C85A32]">{formatFCFA(q.amount)}</strong></p>
                  {q.admin_notes && <p className="text-xs text-slate-500 mt-1">{q.admin_notes}</p>}
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => respond(q.id, "accept")} className="flex-1 bg-[#16A34A] text-white text-sm font-bold py-2 rounded-lg" data-testid={`quote-accept-${q.id}`}>Accepter</button>
                    <button onClick={() => respond(q.id, "modify")} className="flex-1 bg-amber-500 text-white text-sm font-bold py-2 rounded-lg" data-testid={`quote-modify-${q.id}`}>Modifier</button>
                    <button onClick={() => respond(q.id, "refuse")} className="flex-1 bg-slate-200 text-slate-700 text-sm font-bold py-2 rounded-lg" data-testid={`quote-refuse-${q.id}`}>Refuser</button>
                  </div>
                </div>
              )}
              {q.status === "accepte" && q.amount > 0 && <p className="mt-2 text-sm text-green-700 font-semibold">Devis accepté : {formatFCFA(q.amount)}</p>}
            </div>
          ))}
        </div>
      )}

      {showNew && <QuoteRequestModal product={null} onClose={() => { setShowNew(false); load(); }} />}
    </div>
  );
}
